import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiDollarSign } from 'react-icons/fi';
import { apiGet } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import type { MrrItem } from '../types';

interface MrrDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const MrrDrawer: React.FC<MrrDrawerProps> = ({ isOpen, onClose }) => {
  const { data: items, isLoading } = useQuery({
    queryKey: ['admin-dashboard-mrr'],
    queryFn: () => apiGet<MrrItem[]>('/dashboard/mrr-list'),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const total = items?.reduce((sum, i) => sum + i.mrrValue, 0) || 0;
  const subtitle = items
    ? `${items.length} empresa${items.length !== 1 ? 's' : ''} · MRR total ${formatCurrency(total)}`
    : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      icon={<FiDollarSign className="w-5 h-5" />}
      title="MRR — Composição"
      subtitle={subtitle}
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={56} variant="rectangular" />
          ))}
        </div>
      ) : items && items.length > 0 ? (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.companyId}
              className="flex items-center justify-between p-3 rounded-lg"
              style={{ border: '1px solid var(--theme-card-border)' }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {item.companyName}
                </p>
                <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {item.planName}
                </p>
              </div>
              <p className="text-sm font-semibold tabular-nums flex-shrink-0 ml-3" style={{ color: 'var(--theme-primary)' }}>
                {formatCurrency(item.mrrValue)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <FiDollarSign className="w-12 h-12 mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Nenhuma assinatura ativa contribuindo ao MRR
          </p>
        </div>
      )}
    </Drawer>
  );
};
